<template>
    <div
        v-if="tool.name !== 'get_capabilities' && hasContent"
        class="flex flex-col gap-3 items-start"
        :class="tool.name === ACTION.QUERY_RECENT_DOCUMENT_UPLOADS && tool.completed ? 'mt-2' : 'mt-4'"
        :aria-live="tool.name === ACTION.QUERY_RECENT_DOCUMENT_UPLOADS ? 'polite' : undefined">
        <ChatAgentToolCollaboration v-if="tool.name === ACTION.EXPLORE_COLLABORATION" v-bind="tool" />
        <template v-else>
            <NadiaThinkingShapes v-if="usesInlineLoader && !tool.completed" :status-text="label" class="px-2" />
            <Transition name="fade">
                <component :is="toolComponent" v-if="tool.completed" v-bind="tool" :hide-email-button="hideEmailButton" />
            </Transition>
        </template>
    </div>
</template>

<script lang="ts">
import { ACTION, ARTIFACT_TOOL_ACTIONS, isArtifactToolName } from "~vue/chatActions";
import type { ChatMessageLine } from "~vue/types/chat";

const INLINE_LOADER_TOOLS: string[] = [ACTION.QUERY_PAST_PERFORMANCE_REVIEWS, ACTION.ACTION_ITEMS, ACTION.FOLLOW_UP, ACTION.EMAIL_CHAT_SUMMARY, ...ARTIFACT_TOOL_ACTIONS];
const RESULT_CARD_TOOLS: string[] = [
    ACTION.FOLLOW_UP,
    ACTION.EMAIL_CHAT_SUMMARY,
    ACTION.ACTION_ITEMS,
    ACTION.DOCUMENT_CREATOR,
    ACTION.RECOMMEND_LEARNING_CONTENT,
    ACTION.PM_PICKER,
    ACTION.GET_ASSESSMENT_RESULTS,
    ACTION.QUERY_RECENT_DOCUMENT_UPLOADS,
    // read_artifact feeds the model only; its card renders nothing, so a completed read must not claim a row.
    ...ARTIFACT_TOOL_ACTIONS.filter((action) => action !== ACTION.READ_ARTIFACT),
];

// Whether a tool call paints anything right now. ChatMessageList uses it to skip the row
// entirely, since an empty row still emits a labeled assistant article and a gap in the chat.
// Document cards render nothing once nadia_artifacts is off, so their historical calls hide too.
export const toolCallHasContent = (tool: { name: string; completed: boolean; action?: ChatMessageLine | null }, { artifactsEnabled = true, running = true } = {}): boolean => {
    // A failed call has no result for its card to show; Nadia's reply carries the failure.
    if (tool.completed && tool.action?.type === "action" && tool.action.action_name === ACTION.TOOL_FAILED) {
        return false;
    }
    if (tool.name === ACTION.GET_TEAM_PERSPECTIVE) {
        const action = tool.action;
        return (
            tool.completed &&
            action?.type === "action" &&
            action.action_name === ACTION.GET_TEAM_PERSPECTIVE &&
            action.action_params.show_card !== false &&
            (Boolean(action.action_params.selected_team) || ["available", "no_teams"].includes(action.action_params.status))
        );
    }
    // While it runs, the chat's own thinking loader carries the progress; the card only reports the outcome.
    if (tool.name === ACTION.EXPLORE_COLLABORATION) {
        return tool.completed || !running;
    }
    if (
        (tool.name === ACTION.EDIT_ARTIFACT || tool.name === ACTION.REWRITE_ARTIFACT) &&
        tool.action?.type === "action" &&
        (tool.action.action_name === ACTION.EDIT_ARTIFACT || tool.action.action_name === ACTION.REWRITE_ARTIFACT) &&
        tool.action.action_name === tool.name &&
        tool.action.action_params.already_saved_this_turn === true
    ) {
        return false;
    }
    if (tool.completed && tool.action?.type === "action" && tool.action.action_name === tool.name) {
        const result = tool.action.action_params as Record<string, unknown>;
        if ((tool.name === ACTION.SHOW_ARTIFACT && result.already_shown) || (tool.name === ACTION.LIST_ARTIFACTS && result.list_limit_reached === true)) {
            return false;
        }
    }
    if (!artifactsEnabled && isArtifactToolName(tool.name)) {
        return false;
    }
    return tool.completed ? RESULT_CARD_TOOLS.includes(tool.name) : INLINE_LOADER_TOOLS.includes(tool.name);
};

export interface ChatToolCall {
    name: string;
    completed: boolean;
    action?: ChatMessageLine | null;
    messageId?: number | null;
    lineIdx?: number | null;
    statusText?: string | null;
}
</script>

<script setup lang="ts">
import ChatAgentToolArtifact from "~vue/ChatDetail/artifacts/ChatAgentToolArtifact.vue";
import ChatAgentToolActionItems from "~vue/ChatDetail/ChatAgentToolActionItems.vue";
import ChatAgentToolAssessmentResults from "~vue/ChatDetail/ChatAgentToolAssessmentResults.vue";
import ChatAgentToolCollaboration from "~vue/ChatDetail/ChatAgentToolCollaboration.vue";
import ChatAgentToolDocumentCreator from "~vue/ChatDetail/ChatAgentToolDocumentCreator.vue";
import ChatAgentToolEmailChatSummary from "~vue/ChatDetail/ChatAgentToolEmailChatSummary.vue";
import ChatAgentToolFollowup from "~vue/ChatDetail/ChatAgentToolFollowup.vue";
import ChatAgentToolLearningResource from "~vue/ChatDetail/ChatAgentToolLearningResource.vue";
import ChatAgentToolRecentDocuments from "~vue/ChatDetail/ChatAgentToolRecentDocuments.vue";
import ChatPerformancePicker from "~vue/ChatDetail/ChatPerformancePicker.vue";
import ChatTeamPerspectivePicker from "~vue/ChatDetail/ChatTeamPerspectivePicker.vue";
import NadiaThinkingShapes from "~vue/NadiaThinkingShapes.vue";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const props = withDefaults(defineProps<{ tool?: ChatToolCall; hideEmailButton?: boolean; isRunning?: boolean }>(), {
    tool: () => ({ name: "", completed: false }),
    hideEmailButton: false,
    isRunning: true,
});

const { t } = useI18n();

// Only these tools surface progress: the shapes loader carries the generating copy, and the card
// speaks for itself once done — every other tool shows nothing while running.
const usesInlineLoader = computed(() => INLINE_LOADER_TOOLS.includes(props.tool.name));

const toolComponent = computed(() => {
    switch (props.tool.name) {
        case ACTION.FOLLOW_UP:
            return ChatAgentToolFollowup;
        case ACTION.EMAIL_CHAT_SUMMARY:
            return ChatAgentToolEmailChatSummary;
        case ACTION.ACTION_ITEMS:
            return ChatAgentToolActionItems;
        case ACTION.DOCUMENT_CREATOR:
            return ChatAgentToolDocumentCreator;
        case ACTION.RECOMMEND_LEARNING_CONTENT:
            return ChatAgentToolLearningResource;
        case ACTION.PM_PICKER:
            return ChatPerformancePicker;
        case ACTION.GET_TEAM_PERSPECTIVE:
            return ChatTeamPerspectivePicker;
        case ACTION.GET_ASSESSMENT_RESULTS:
            return ChatAgentToolAssessmentResults;
        case ACTION.QUERY_RECENT_DOCUMENT_UPLOADS:
            return ChatAgentToolRecentDocuments;
        case ACTION.CREATE_ARTIFACT:
        case ACTION.EDIT_ARTIFACT:
        case ACTION.REWRITE_ARTIFACT:
        case ACTION.READ_ARTIFACT:
        case ACTION.LIST_ARTIFACTS:
        case ACTION.SHOW_ARTIFACT:
            return ChatAgentToolArtifact;
    }

    return null;
});

const hasContent = computed(() => toolCallHasContent(props.tool, { running: props.isRunning }));

// WorkIQ tools are deliberately absent from both label maps: they render through the inline
// progress line in ChatMessageList (useWorkIqToolProgress), never through this pill.
const label = computed(() => {
    if (props.tool.completed) {
        switch (props.tool.name) {
            case ACTION.FOLLOW_UP:
                return t("chat.toolCall.completed.invite");
            case ACTION.EMAIL_CHAT_SUMMARY:
                return t("chat.toolCall.completed.summary");
            case ACTION.ACTION_ITEMS:
                return t("chat.toolCall.completed.actionItem");
            case ACTION.DOCUMENT_CREATOR:
                return t("chat.toolCall.completed.document");
            case ACTION.RECOMMEND_LEARNING_CONTENT:
                return t("chat.toolCall.completed.learningResource");
            case "list_meeting_transcripts":
                return t("chat.toolCall.completed.meetings");
            case "get_meeting_outlines":
                return t("chat.toolCall.completed.meetingDetails");
            case "get_meeting_transcript":
                return t("chat.toolCall.completed.meetingTranscript");
            default:
                return t("chat.toolCall.completed.default");
        }
    }

    // Live progress text (e.g. Work IQ's own "Checking your calendar..." lifecycle
    // messages) takes priority over the static per-tool label while running.
    if (props.tool.statusText) {
        return props.tool.statusText;
    }

    switch (props.tool.name) {
        case ACTION.FOLLOW_UP:
            return t("chat.toolCall.running.inviteGenerating");

        case ACTION.EMAIL_CHAT_SUMMARY:
            return t("chat.toolCall.running.summary");

        case ACTION.ACTION_ITEMS:
            return t("chat.toolCall.running.actionItem");

        case ACTION.DOCUMENT_CREATOR:
            return t("chat.toolCall.running.document");

        case ACTION.RECOMMEND_LEARNING_CONTENT:
            return t("chat.toolCall.running.learningResource");

        case ACTION.CREATE_ARTIFACT:
            return t("chat.toolCall.running.savingDocument");

        case ACTION.EDIT_ARTIFACT:
        case ACTION.REWRITE_ARTIFACT:
            return t("chat.toolCall.running.updatingDocument");

        case ACTION.READ_ARTIFACT:
            return t("chat.toolCall.running.readingDocument");

        case ACTION.LIST_ARTIFACTS:
            return t("chat.toolCall.running.listingDocuments");

        case ACTION.SHOW_ARTIFACT:
            return t("chat.toolCall.running.openingDocument");

        case "list_meeting_transcripts":
            return t("chat.toolCall.running.meetings");

        case "get_meeting_outlines":
            return t("chat.toolCall.running.meetingDetails");

        case "get_meeting_transcript":
            return t("chat.toolCall.running.meetingTranscript");

        case ACTION.PM_PICKER:
            return t("chat.toolCall.running.performanceTasks");

        case ACTION.GET_ASSESSMENT_RESULTS:
            return t("chat.toolCall.running.assessmentResults");

        case ACTION.QUERY_RECENT_DOCUMENT_UPLOADS:
            return t("chat.toolCall.running.recentUploads");

        case ACTION.QUERY_PAST_PERFORMANCE_REVIEWS:
            return t("chat.toolCall.running.pastPerformanceReviews");

        default:
            return t("chat.toolCall.running.default");
    }
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 300ms ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

i.bi::before {
    font-weight: bold !important;
}
</style>
